import { __awaiter } from "tslib";
import { derived, writable } from "../util/customStores";
import { instructionToActions } from "./actions/instructionToActionConverter";
import { LAST_ADDRESS } from "../util/ram";
import { messageFeedStore } from "../store/state";
import { FETCH, INCREMENT_PC } from "./actions/presets";
import logger, { LogCategory } from "../util/logger";
/** Implements the flow of the program's execution */
export default class ProgramExecution {
    /**
     * @param cpu - Reference to the cpu used to perform the actions
     * @param ram - Reference to the ram used to perform the actions
     * @param symbolTable - Reference to the symbol table used to perform the actions
     * @param wires - Reference to the wires used to perform the actions
     */
    constructor(cpu, ram, symbolTable, wires) {
        if (!cpu)
            throw new Error("Null or undefined cpu");
        if (!ram)
            throw new Error("Null or undefined ram");
        if (!symbolTable)
            throw new Error("Null or undefined symbolTable");
        if (!wires)
            throw new Error("Null or undefined wires");
        this._isExecuting = writable(false);
        this.isExecuting = derived(this._isExecuting, value => value);
        this.isPlayingProgram = false;
        this.isPlayingMicrostep = false;
        this.isPlayingInstruction = false;
        this.cyclePhase = "ENQUEUING_FETCH";
        this.queue = [];
        this.executionContext = {
            cpu: {
                model: cpu,
                component: null // will be set when application mounts
            },
            ram: {
                model: ram,
                component: null // will be set when application mounts
            },
            wires: {
                model: wires,
                component: null // will be set when application mounts
            },
            symbolTable,
            stepTextComponent: null // will be set when application mounts
        };
        this.executionEndedCallbacks = [];
    }
    cycle() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.shouldExecute()) {
                return;
            }
            try {
                switch (this.cyclePhase) {
                    case "ENQUEUING_FETCH":
                        this.queue.push(...FETCH);
                        this.cyclePhase = "EXECUTING_FETCH";
                        break;
                    case "EXECUTING_FETCH":
                        yield this.execute();
                        if (this.queueIsEmpty()) {
                            this.cyclePhase = "ENQUEUING_INSTRUCTION";
                        }
                        break;
                    case "ENQUEUING_INSTRUCTION":
                        const ir = this.executionContext.cpu.model.instructionRegister.get();
                        this.queue.push(...instructionToActions(ir));
                        logger.debug(`Executing instruction "${ir.symbolic()}"`, LogCategory.EXECUTION);
                        this.cyclePhase = "EXECUTING_INSTRUCTION";
                        break;
                    case "EXECUTING_INSTRUCTION":
                        yield this.execute();
                        if (this.queueIsEmpty()) {
                            if (this.executionContext.cpu.model.programCounter.get().unsigned() === LAST_ADDRESS &&
                                !this.executionContext.cpu.model.isJumping.get()) {
                                this.executionContext.cpu.model.isHalting.set(true);
                            }
                            if (this.executionContext.cpu.model.isJumping.get()) {
                                this.executionContext.cpu.model.isJumping.set(false);
                                this.cyclePhase = "ENQUEUING_FETCH";
                                if (this.isPlayingInstruction) {
                                    this.setIsPlayingInstruction(false);
                                }
                            }
                            else {
                                this.cyclePhase = "ENQUEUEING_PC_INCREMENT";
                            }
                        }
                        break;
                    case "ENQUEUEING_PC_INCREMENT":
                        this.queue.push(...INCREMENT_PC);
                        this.cyclePhase = "EXECUTING_PC_INCREMENT";
                        break;
                    case "EXECUTING_PC_INCREMENT":
                        yield this.execute();
                        if (this.isPlayingInstruction) {
                            this.setIsPlayingInstruction(false);
                            this.executionContext.cpu.model.isHalting.set(true);
                        }
                        if (this.queueIsEmpty()) {
                            this.cyclePhase = "ENQUEUING_FETCH";
                        }
                        break;
                }
            }
            catch (error) {
                this.executionContext.cpu.model.isHalting.set(true);
                messageFeedStore.get().error(error.message);
                logger.handled_error(error.message, LogCategory.EXECUTION);
            }
            finally {
                if (this.executionContext.cpu.model.isHalting.get()) {
                    this.reset();
                }
            }
        });
    }
    /** Execute the actions in queue */
    execute() {
        return __awaiter(this, void 0, void 0, function* () {
            while (!this.queueIsEmpty() && this.shouldExecute()) {
                const action = this.queue.shift();
                yield action.execute(this.executionContext);
                if (this.isPlayingMicrostep && action.doesEndStep()) {
                    this.setIsPlayingMicrostep(false);
                }
            }
        });
    }
    /** Start the execution of the program. The program will stop only if it encounters an error, reaches its end or the execution is paused */
    start(...executionEndedCallbacks) {
        this.executionEndedCallbacks.push(...executionEndedCallbacks);
        this.setIsPlayingProgram(true);
        this.setIsPlayingMicrostep(false);
        this.setIsPlayingInstruction(false);
        logger.debug("Execution - START", LogCategory.EXECUTION);
    }
    /** Pauses the execution of the program */
    pause() {
        this.setIsPlayingProgram(false);
        this.setIsPlayingMicrostep(false);
        this.setIsPlayingInstruction(false);
        this.executeExecutionEndedCallbacks();
        logger.debug("Execution - PAUSE", LogCategory.EXECUTION);
    }
    /** Toggles the state of the execution between executing and paused */
    toggle(...executionEndedCallbacks) {
        if (!this.isExecuting.get()) {
            this.start(...executionEndedCallbacks);
        }
        else {
            this.pause();
        }
    }
    /** Executes a single microstep */
    step(...executionEndedCallbacks) {
        this.executionEndedCallbacks.push(...executionEndedCallbacks);
        this.setIsPlayingProgram(false);
        this.setIsPlayingMicrostep(true);
        this.setIsPlayingInstruction(false);
        logger.debug("Execution - STEP", LogCategory.EXECUTION);
    }
    /** Executes a single instruction */
    instruction(...executionEndedCallbacks) {
        this.executionEndedCallbacks.push(...executionEndedCallbacks);
        this.setIsPlayingProgram(false);
        this.setIsPlayingMicrostep(false);
        this.setIsPlayingInstruction(true);
        logger.debug("Execution - INSTRUCTION", LogCategory.EXECUTION);
    }
    /** Pauses and reset the execution. All actions ready to be executed won't be executed */
    reset() {
        this.pause();
        this.executionContext.cpu.model.isHalting.set(false);
        this.executionContext.cpu.model.isJumping.set(false);
        this.cyclePhase = "ENQUEUING_FETCH";
        this.emptyExecutionQueue();
        logger.debug("Execution - RESET", LogCategory.EXECUTION);
    }
    /** Returns true if the execution should execute actions */
    shouldExecute() {
        return this.isPlayingProgram || this.isPlayingMicrostep || this.isPlayingInstruction;
    }
    /** Empties the execution queue */
    emptyExecutionQueue() {
        this.queue = [];
    }
    /** Returns true if the execution queue is empty */
    queueIsEmpty() {
        return this.queue.length === 0;
    }
    /** Sets the isPlayingProgram variable and updates the isExecuting store */
    setIsPlayingProgram(value) {
        this.isPlayingProgram = value;
        this._isExecuting.set(this.isPlayingProgram || this.isPlayingMicrostep || this.isPlayingInstruction);
    }
    /** Sets the isPlayingMicrostep variable and updates the isExecuting store */
    setIsPlayingMicrostep(value) {
        this.isPlayingMicrostep = value;
        this._isExecuting.set(this.isPlayingProgram || this.isPlayingMicrostep || this.isPlayingInstruction);
    }
    /** Sets the isPlayingInstruction variable and updates the isExecuting store */
    setIsPlayingInstruction(value) {
        this.isPlayingInstruction = value;
        this._isExecuting.set(this.isPlayingProgram || this.isPlayingMicrostep || this.isPlayingInstruction);
    }
    /** Executes all the executionEndedCallbacks, called each time the execution is paused */
    executeExecutionEndedCallbacks() {
        this.executionEndedCallbacks.forEach(callback => callback(this.executionContext));
        this.executionEndedCallbacks = [];
    }
}
